// Which row the card's Edit button edits.
//
// It used to be "the branch row with no dates on it". That is only true until
// the first edit: saving a live rule closes it (effective_to = yesterday) and
// starts a successor from today, so afterwards NO row is undated, the button
// silently became "Configure", and the next Save went through create — which
// wrote a brand-new row with no dates at all. Two "from always" rows tie on
// everything but creation time, so the newer one won for every PAST day too.
// That is how switching the late-coming pause off on 16 Sep switched it off for
// the whole of September, and would have deducted nine people's pay the next
// morning.
//
// The edit target is therefore the branch-wide, unpredicated row that is in
// force TODAY — chosen the way the resolver chooses: latest start date, then
// most recently created. Rows scoped to people are exceptions and are edited
// from their own line.

import type { PolicyRuleRow } from '../../api/policyRules';

/** Local calendar date as YYYY-MM-DD (lexically comparable to the API's dates). */
export function localTodayISO(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function isBaseRow(row: PolicyRuleRow): boolean {
  return row.scope === 'branch' && Object.keys(row.applies_to ?? {}).length === 0;
}

export function inForceOn(row: PolicyRuleRow, dayISO: string): boolean {
  if (row.effective_from && row.effective_from > dayISO) return false;
  if (row.effective_to && row.effective_to < dayISO) return false;
  return true;
}

/**
 * The base row Edit should open, or null when the branch has none in force
 * today (then the button is "Configure" and Save creates one).
 */
export function editableBaseRow(
  rows: PolicyRuleRow[],
  todayISO: string = localTodayISO(),
): PolicyRuleRow | null {
  const live = rows.filter((r) => isBaseRow(r) && inForceOn(r, todayISO));
  if (live.length === 0) return null;
  // Same ranking as the resolver: priority, then latest start, then newest.
  live.sort((a, b) =>
    (b.priority ?? 0) - (a.priority ?? 0)
    || (b.effective_from ?? '').localeCompare(a.effective_from ?? '')
    || (b.created_at ?? '').localeCompare(a.created_at ?? ''));
  return live[0];
}

/**
 * The in-force pause window's end for a `late.suspension` rule, for the card's
 * "paused till …" line. `active` mirrors what the engine resolves today.
 */
export function pauseState(
  effectiveParams: Record<string, unknown> | null,
  rows: PolicyRuleRow[],
  todayISO: string = localTodayISO(),
): { active: boolean; until: string | null } {
  if (!effectiveParams || !effectiveParams.suspended) return { active: false, until: null };
  const winner = editableBaseRow(rows, todayISO);
  return { active: true, until: winner?.effective_to ?? null };
}
